import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import api from "../api";
import { Bt, Sl, Inp, SearchSelect } from "./index";
import { notify, confirmDialog } from "../notify";

const STATUSES = ["Present", "Late", "Half Day", "WFH", "Absent"];
const NEEDS_IN = ["Present", "Late", "Half Day"];

// Mark / correct / clear anyone's attendance for a day. Shown only with the permission
// Attendance → Today's Overview → "Mark / Edit Attendance Manually".
export const ManualAttendanceModal = ({ employees = [], initial = {}, canDelete = false, onClose, onSaved }) => {
  const todayYmd = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };
  const [empId, setEmpId] = useState(initial.employeeId || "");
  const [date, setDate] = useState(initial.date || todayYmd());
  const [status, setStatus] = useState("Present");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [overnight, setOvernight] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Pre-fill with whatever is already recorded for this person and day.
  useEffect(() => {
    if (!empId || !date) return;
    let live = true;
    setLoading(true); setErr("");
    api.get(`/api/attendance/manual/${encodeURIComponent(empId)}/${date}`)
      .then((d) => {
        if (!live) return;
        setExists(!!d.exists);
        setStatus(d.exists && STATUSES.includes(d.status) ? d.status : "Present");
        setCheckIn(d.check_in || ""); setCheckOut(d.check_out || "");
        setRemarks(""); setOvernight(false);
      })
      .catch((e) => { if (live) setErr(e.message || "Couldn't load this day"); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [empId, date]);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await api.put("/api/attendance/manual", {
        employee_id: empId, date, status,
        check_in: status === "Absent" ? null : checkIn || null,
        check_out: status === "Absent" ? null : checkOut || null,
        overnight, remarks: remarks.trim() || null,
      });
      notify("Attendance updated", { title: "Saved", variant: "success" });
      onSaved?.(); onClose();
    } catch (e) { setErr(e.message || "Couldn't save"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!(await confirmDialog("Remove this day's attendance record completely? The person will then show as not checked in / absent."))) return;
    setSaving(true); setErr("");
    try {
      await api.del(`/api/attendance/manual/${encodeURIComponent(empId)}/${date}`);
      notify("Attendance record removed", { title: "Saved", variant: "success" });
      onSaved?.(); onClose();
    } catch (e) { setErr(e.message || "Couldn't remove"); }
    finally { setSaving(false); }
  };

  const showTimes = status !== "Absent";
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[520px] max-h-[88vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <span className="text-base font-extrabold text-t0">Mark attendance manually</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>

        <div className="mb-[10px]">
          <div className="text-[14px] text-t2 mb-[3px]">Employee</div>
          <SearchSelect options={employees} value={empId} onChange={setEmpId} placeholder="Choose an employee" searchPlaceholder="Search by name or ID…" />
        </div>
        <div className="grid grid-cols-2 gap-3 mb-[10px]">
          <div>
            <div className="text-[14px] text-t2 mb-[3px]">Date</div>
            <Inp type="date" value={date} onChange={setDate} />
          </div>
          <div>
            <div className="text-[14px] text-t2 mb-[3px]">Status</div>
            <Sl options={STATUSES.map((s) => ({ v: s, l: s }))} value={status} onChange={setStatus} />
          </div>
        </div>
        {showTimes && <div className="grid grid-cols-2 gap-3 mb-[6px]">
          <div>
            <div className="text-[14px] text-t2 mb-[3px]">Check-in time {NEEDS_IN.includes(status) ? "" : <span className="text-t3">(optional)</span>}</div>
            <Inp type="time" value={checkIn} onChange={setCheckIn} />
          </div>
          <div>
            <div className="text-[14px] text-t2 mb-[3px]">Check-out time <span className="text-t3">(optional)</span></div>
            <Inp type="time" value={checkOut} onChange={setCheckOut} />
          </div>
        </div>}
        {showTimes && <label className="flex items-center gap-2 text-[13px] text-t2 mb-[10px] cursor-pointer">
          <input type="checkbox" checked={overnight} onChange={(e) => setOvernight(e.target.checked)} />Check-out is the next day (night shift)
        </label>}
        <div className="mb-[10px]">
          <div className="text-[14px] text-t2 mb-[3px]">Remarks <span className="text-t3">(optional)</span></div>
          <Inp placeholder="Why is this being changed?" value={remarks} onChange={setRemarks} />
        </div>
        {status === "Absent" && <div className="text-[13px] text-t3 mb-[10px]">Marking Absent clears the check-in and check-out times for this day.</div>}
        {empId && !loading && exists && <div className="text-[13px] text-t3 mb-[10px]">This day already has a record — saving will replace it.</div>}
        {err && <div className="px-3 py-2 mb-3 rounded-lg text-[14px] font-semibold bg-rb text-r border border-rd">{err}</div>}

        <div className="flex gap-2 justify-end flex-wrap">
          {canDelete && exists && <Bt v="danger" s="sm" disabled={saving} onClick={remove}>Remove record</Bt>}
          <Bt v="secondary" s="sm" disabled={saving} onClick={onClose}>Cancel</Bt>
          <Bt v="success" s="sm" disabled={saving || loading || !empId || !date} onClick={save}>{saving ? "Saving…" : "Save"}</Bt>
        </div>
      </div>
    </div>
  );
};
